import { Trash2, AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

// Radix Presence unmounts synchronously once the closed-state animation-name resolves to none.
const NO_EXIT_ANIMATION = 'data-[state=closed]:animate-none data-[state=closed]:duration-0';

const typeConfig = {
  destructive: {
    icon: Trash2,
    iconClass: 'text-destructive',
    buttonVariant: 'destructive' as const,
    defaultLabel: 'Delete',
  },
  warning: {
    icon: AlertTriangle,
    iconClass: 'text-warning',
    buttonVariant: 'default' as const,
    defaultLabel: 'Confirm',
  },
  default: {
    icon: Info,
    iconClass: 'text-primary',
    buttonVariant: 'default' as const,
    defaultLabel: 'Confirm',
  },
};

const ConfirmDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  type?: 'destructive' | 'warning' | 'default';
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  loading?: boolean;
  instantClose?: boolean;
}> = ({
  open,
  onOpenChange,
  title,
  description,
  type = 'default',
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  loading = false,
  instantClose = false,
}) => {
  const config = typeConfig[type];
  const Icon = config.icon;

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(instantClose && NO_EXIT_ANIMATION)}
        overlayClassName={cn(instantClose && NO_EXIT_ANIMATION)}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`size-5 ${config.iconClass}`} />
            {title}
          </DialogTitle>
          <DialogDescription className="text-left">{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            {cancelLabel}
          </Button>
          <Button variant={config.buttonVariant} onClick={onConfirm} loading={loading}>
            {confirmLabel ?? config.defaultLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { ConfirmDialog };
